import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "../../config/api";
import "./CompleteAccount.css";
import { getAccountCompletionDestination } from "../../utils/authFlow";

import { notifyAuthChanged } from "../../utils/authSession";
import useUsernameAvailability from "../../hooks/useUsernameAvailability";

const CompleteAccount = () => {
  const [verifiedMobile] = useState(() => sessionStorage.getItem("pendingMobileNumber") || localStorage.getItem("tempMobileNumber") || "");
  const [formData, setFormData] = useState({
    mobile_number: verifiedMobile,
    full_name: "",
    email: "",
    username: "",
    is_terms_accepted: false,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const navigate = useNavigate();
  const { status: usernameStatus, retry: retryUsername } = useUsernameAvailability(formData.username);
  const usernameMessage = {
    idle: 'Minimum 3 characters',
    checking: 'Checking username…',
    available: '',
    unavailable: 'Username already taken. Please choose another.',
    error: 'Could not check availability. Please try again.',
  }[usernameStatus];

  // Access URL search parameters to handle post-account-completion redirect
  const [searchParams] = useSearchParams();
  // The 'redirect-to' parameter specifies the target path after account setup
  const pageParam = searchParams.get("redirect-to");

  const eventCode = localStorage.getItem("eventCode");
  const isEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim());
  const validateForm = () => {
    if (!/^[0-9]{10}$/.test(formData.mobile_number)) {
      setError("Please enter your 10-digit mobile number.");
      return false;
    }
    if (!formData.full_name.trim()) {
      setError("Full name is required");
      return false;
    }
    if (!isEmailValid) {
      setError("Please enter a valid email address");
      return false;
    }
    if (!formData.username.trim()) {
      setError("Username is required");
      return false;
    }
    if (formData.username.length < 3) {
      setError("Username must be at least 3 characters");
      return false;
    }
    if (!formData.is_terms_accepted) {
      setError("You must accept the terms and conditions");
      return false;
    }
    if (usernameStatus !== 'available') {
      setError('Please choose an available username before continuing.');
      return false;
    }
    return true;
  };

  const showSignupError = (data) => {
    const messages = {};
    if (data?.errors && typeof data.errors === "object") {
      Object.entries(data.errors).forEach(([field, value]) => {
        const message = Array.isArray(value) ? value.filter((item) => typeof item === "string").join(" ") : value;
        if (typeof message === "string" && message.trim()) messages[field] = message;
      });
    }
    setFieldErrors(messages);
    const otherErrors = Object.entries(messages).filter(([field]) => !(field in formData)).map(([, message]) => message);
    const general = typeof data?.message === "string" ? data.message : "";
    setError([general, ...otherErrors].filter(Boolean).join(" ") || (Object.keys(messages).length ? "" : "Account completion failed. Please try again."));
  };

  const fieldError = (name) => fieldErrors[name] && (
    <small id={`${name}-error`} className="signup-field-error" role="alert">{fieldErrors[name]}</small>
  );
  const errorAttributes = (name) => ({
    'aria-invalid': Boolean(fieldErrors[name]),
    'aria-describedby': fieldErrors[name] ? `${name}-error` : undefined,
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setError("");
    setFieldErrors({});

    if (!validateForm()) {
      return;
    }

    const signupToken = sessionStorage.getItem("pendingAuthToken") || localStorage.getItem("authToken");
    if (!signupToken) {
      setError("Your verification session has expired. Please verify your mobile number again.");
      return;
    }
    setLoading(true);

    try {
      const response = await api.post("/users/signup", {
        mobile_number: formData.mobile_number,
        full_name: formData.full_name,
        email: formData.email.trim(),
        username: formData.username.trim(),
        is_terms_accepted: formData.is_terms_accepted,
      }, { headers: { Authorization: `Bearer ${signupToken}` } });

      if (response.data.status === "SUCCESS") {
        localStorage.setItem("authToken", signupToken);
        // Mark account as complete
        localStorage.setItem("isCompleteAccount", "true");

        // Clean up temporary mobile number
        localStorage.removeItem("tempMobileNumber");
        sessionStorage.removeItem("pendingAuthToken");
        sessionStorage.removeItem("pendingMobileNumber");
        notifyAuthChanged();

        navigate(getAccountCompletionDestination(eventCode, pageParam), { replace: true });
      } else {
        showSignupError(response.data);
      }
    } catch (err) {
      showSignupError(err.response?.data);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFieldErrors((prev) => ({ ...prev, [name]: undefined }));
    setError("");
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const isFormValid =
    usernameStatus === 'available' &&
    isEmailValid &&
    formData.full_name.trim() &&
    formData.username.trim() &&
    formData.is_terms_accepted &&
    formData.username.length >= 3;

  return (
    <>

      <div className="complete-account-container">
        <div className="complete-account-card">
          <h2>Complete Your Account</h2>
          <p>Please provide your details to complete the account setup</p>

          <form onSubmit={handleSubmit} className="complete-account-form">
            <div className="form-group">
              <label htmlFor="mobile_number">Mobile Number</label>
              <input
                type="tel"
                id="mobile_number"
                name="mobile_number"
                {...errorAttributes("mobile_number")}
                value={formData.mobile_number}
                readOnly={Boolean(verifiedMobile)}
                onChange={handleInputChange}
                inputMode="numeric"
                maxLength="10"
                required
              />
              {fieldError("mobile_number")}
            </div>

            <div className="form-group">
              <label htmlFor="full_name">Full Name *</label>
              <input
                type="text"
                id="full_name"
                name="full_name"
                {...errorAttributes("full_name")}
                value={formData.full_name}
                onChange={handleInputChange}
                placeholder="Enter your full name"
                required
              />
              {fieldError("full_name")}
            </div>

            <div className="form-group">
              <label htmlFor="email">Email *</label>
              <input
                type="email"
                id="email"
                name="email"
                {...errorAttributes("email")}
                value={formData.email}
                onChange={handleInputChange}
                autoComplete="email"
                autoCapitalize="none"
                spellCheck={false}
                placeholder="Enter your email address"
                required
              />
              {fieldError("email")}
            </div>

            <div className="form-group">
              <label htmlFor="username">Username *</label>
              <input
                type="text"
                id="username"
                name="username"
                autoComplete="username"
                autoCapitalize="none"
                spellCheck={false}
                aria-describedby={[fieldErrors.username && 'username-error', usernameMessage && 'username-availability'].filter(Boolean).join(' ') || undefined}
                aria-invalid={Boolean(fieldErrors.username) || usernameStatus === 'unavailable'}
                value={formData.username}
                onChange={handleInputChange}
                placeholder="Enter a username"
                required
              />
              {fieldError("username")}
              {usernameMessage && <small id="username-availability" className={`username-availability ${usernameStatus}`} role="status">{usernameMessage}</small>}
              {usernameStatus === 'error' && <button className="username-retry" type="button" onClick={retryUsername}>Retry check</button>}
            </div>

            <div className="form-group">
              <label className={`terms-consent${formData.is_terms_accepted ? ' terms-consent-checked' : ''}`}>
                <input
                  type="checkbox"
                  name="is_terms_accepted"
                  {...errorAttributes("is_terms_accepted")}
                  checked={formData.is_terms_accepted}
                  onChange={handleInputChange}
                  required
                  disabled={loading}
                />
                <span>I accept the{" "}
                  <a href="https://www.clickevents.app/terms-and-conditions/" target="_blank" rel="noopener noreferrer">
                    Terms & Conditions
                  </a>
                </span>
              </label>
              {fieldError("is_terms_accepted")}
            </div>

            {error && <div className="error-message" role="alert">{error}</div>}

            <button
              type="submit"
              className="submit-btn"
              disabled={loading || !isFormValid}
            >
              {loading ? "Submitting..." : "Complete Account"}
            </button>
          </form>
        </div>
      </div>
    </>
  );
};

export default CompleteAccount;
